import { Link } from "react-router-dom";
import { useState } from "react";
import { useCart, cartKey } from "@/context/CartContext";
import { api } from "@/lib/api";
import { Minus, Plus, X, Truck, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { CART } from "@/constants/testIds";
import { resolveImage } from "@/lib/siteDefaults";

export default function Cart() {
  const { items, subtotal, updateQty, remove } = useCart();
  const [loading, setLoading] = useState(false);
  const [zip, setZip] = useState("");
  const [state, setState] = useState("");
  const [country, setCountry] = useState("US");
  const [ratesLoading, setRatesLoading] = useState(false);
  const [rates, setRates] = useState([]);
  const [selectedRateId, setSelectedRateId] = useState("");
  const [freeShipApplied, setFreeShipApplied] = useState(false);

  const fetchRates = async () => {
    if (items.length === 0) return;
    if (!zip.trim()) {
      toast.error("Enter a ZIP code first");
      return;
    }
    setRatesLoading(true);
    setRates([]);
    setSelectedRateId("");
    try {
      const payload = {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity, size: i.size, color: i.color })),
        to_zip: zip.trim(),
        to_state: state.trim() || null,
        to_country: country || "US",
      };
      const { data } = await api.post("/shipping/estimate", payload);
      setFreeShipApplied(!!data.free_shipping_applied);
      setRates(data.rates || []);
      if (data.rates?.length) setSelectedRateId(data.rates[0].id);
      if (data.free_shipping_applied) toast.success("You qualify for free shipping!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't fetch rates");
    } finally {
      setRatesLoading(false);
    }
  };

  const selectedRate = rates.find((r) => r.id === selectedRateId);

  const checkout = async () => {
    if (items.length === 0) return;
    if (!selectedRate) {
      toast.error("Please pick a shipping option before checking out.");
      // Scroll the estimator into view so it's obvious what to do
      document.querySelector('[data-testid="cart-get-rates"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setLoading(true);
    try {
      const payload = {
        items: items.map((i) => ({
          product_id: i.product_id,
          quantity: i.quantity,
          size: i.size,
          color: i.color,
        })),
        origin_url: window.location.origin,
        shipping: {
          label: `${selectedRate.carrier} · ${selectedRate.service}`,
          amount_cents: Math.round(parseFloat(selectedRate.rate) * 100),
          delivery_days: selectedRate.delivery_days || 3,
          carrier: selectedRate.carrier,
          service: selectedRate.service,
          shippo_rate_id: selectedRate.id !== "free_shipping" ? selectedRate.id : null,
        },
      };
      const { data } = await api.post("/checkout/create-session", payload, { withCredentials: true });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed. Try again.");
      setLoading(false);
    }
  };

  return (
    <div className="pt-24 md:pt-32 pb-24 md:pb-32 min-h-screen">
      <div className="max-w-[1200px] mx-auto px-6 md:px-12">
        <p className="text-xs uppercase tracking-[0.3em] text-stone-500 font-semibold mb-4">Your cart</p>
        <h1 className="font-serif text-5xl md:text-6xl font-light tracking-tighter text-[#1A1C1A]">
          The <em className="italic text-stone-600">collection</em>.
        </h1>

        {items.length === 0 ? (
          <div className="mt-16 py-24 text-center border-t border-stone-200">
            <p data-testid={CART.emptyMessage} className="font-serif text-3xl italic text-stone-500 mb-6">
              Your cart is quiet.
            </p>
            <Link
              to="/shop"
              className="inline-flex items-center gap-2 bg-[#1A1C1A] text-[#F9F9F6] hover:bg-[#3C3E3C] px-8 py-3.5 rounded-full transition-all font-sans text-sm tracking-wide"
            >
              Discover the collective
            </Link>
          </div>
        ) : (
          <div className="mt-12 md:mt-16 grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2 border-t border-stone-200">
              {items.map((item) => {
                const key = cartKey(item);
                return (
                  <div
                    key={key}
                    data-testid={CART.itemRow}
                    className="py-6 border-b border-stone-200 flex gap-4 md:gap-6"
                  >
                    <div className="w-24 h-32 md:w-28 md:h-36 rounded-xl overflow-hidden bg-[#F4F1EA] flex-shrink-0">
                      {item.image && <img src={resolveImage(item.image)} alt={item.name} className="w-full h-full object-cover" />}
                    </div>
                    <div className="flex-1 flex flex-col justify-between">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <h3 data-testid={CART.itemName} className="font-serif text-xl md:text-2xl font-light tracking-tight text-[#1A1C1A]">
                            {item.name}
                          </h3>
                          <div className="mt-1 text-xs text-stone-500 font-sans space-x-3">
                            {item.size && <span>Size {item.size}</span>}
                            {item.color && <span>· {item.color}</span>}
                          </div>
                        </div>
                        <p className="font-sans text-sm text-[#1A1C1A] whitespace-nowrap">
                          ${(item.price * item.quantity).toFixed(2)}
                        </p>
                      </div>
                      <div className="flex items-center justify-between mt-4">
                        <div className="flex items-center border border-stone-300 rounded-full">
                          <button
                            data-testid={CART.itemDecrease}
                            onClick={() => updateQty(key, item.quantity - 1)}
                            className="w-9 h-9 flex items-center justify-center hover:bg-[#F4F1EA] rounded-l-full"
                          >
                            <Minus strokeWidth={1.5} className="w-3.5 h-3.5" />
                          </button>
                          <span data-testid={CART.itemQty} className="w-8 text-center font-sans text-sm">
                            {item.quantity}
                          </span>
                          <button
                            data-testid={CART.itemIncrease}
                            onClick={() => {
                              if (item.stock != null && item.quantity >= item.stock) {
                                toast.error(`Only ${item.stock} available`);
                                return;
                              }
                              updateQty(key, item.quantity + 1);
                            }}
                            disabled={item.stock != null && item.quantity >= item.stock}
                            className="w-9 h-9 flex items-center justify-center hover:bg-[#F4F1EA] rounded-r-full disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            <Plus strokeWidth={1.5} className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        <button
                          data-testid={CART.itemRemove}
                          onClick={() => remove(key)}
                          className="text-xs uppercase tracking-wide text-stone-500 hover:text-[#1A1C1A] flex items-center gap-1.5 transition-colors"
                        >
                          <X strokeWidth={1.5} className="w-3.5 h-3.5" /> Remove
                        </button>
                      </div>
                      {item.stock != null && item.stock > 0 && item.stock <= 5 && (
                        <p className="mt-2 text-xs text-[#8B6A1A]">Only {item.stock} left in stock</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="lg:col-span-1">
              <div className="bg-[#F4F1EA] rounded-2xl p-8 md:sticky md:top-28">
                <p className="text-xs uppercase tracking-[0.2em] text-stone-500 font-semibold mb-6">Summary</p>
                <div className="flex justify-between font-sans text-sm mb-3">
                  <span className="text-stone-600">Subtotal</span>
                  <span data-testid={CART.subtotal} className="text-[#1A1C1A]">${subtotal.toFixed(2)}</span>
                </div>

                {/* Shipping estimator */}
                <div className="mb-4 pt-4 border-t border-stone-300/60">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-stone-500 font-semibold mb-3 flex items-center gap-1.5">
                    <Truck className="w-3 h-3" strokeWidth={2} /> Shipping to
                  </p>
                  <div className="grid grid-cols-3 gap-2">
                    <input
                      data-testid="cart-ship-zip"
                      value={zip}
                      onChange={(e) => setZip(e.target.value)}
                      placeholder="ZIP"
                      className="col-span-2 px-3 py-2 rounded-full border border-stone-300 bg-white text-sm placeholder:text-stone-400 focus:outline-none focus:border-[#1A1C1A]"
                    />
                    <input
                      data-testid="cart-ship-state"
                      value={state}
                      onChange={(e) => setState(e.target.value.toUpperCase().slice(0, 2))}
                      placeholder="ST"
                      className="px-3 py-2 rounded-full border border-stone-300 bg-white text-sm placeholder:text-stone-400 focus:outline-none focus:border-[#1A1C1A]"
                    />
                  </div>
                  <button
                    onClick={fetchRates}
                    disabled={ratesLoading || items.length === 0}
                    data-testid="cart-get-rates"
                    className="mt-2 w-full text-xs uppercase tracking-wider font-sans py-2 px-4 rounded-full border border-stone-400 hover:bg-[#F4F1EA] transition-colors disabled:opacity-50"
                  >
                    {ratesLoading ? <span className="inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Getting rates…</span> : rates.length > 0 ? "Refresh rates" : "Get shipping rates"}
                  </button>

                  {rates.length > 0 && (
                    <div className="mt-3 space-y-1.5" data-testid="cart-rates-list">
                      {rates.map((r) => (
                        <label
                          key={r.id}
                          className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer hover:bg-[#F4F1EA] transition-colors ${selectedRateId === r.id ? "bg-[#F4F1EA]" : ""}`}
                        >
                          <input
                            type="radio"
                            name="cart-rate"
                            value={r.id}
                            checked={selectedRateId === r.id}
                            onChange={() => setSelectedRateId(r.id)}
                            data-testid={`cart-rate-${r.id}`}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="text-xs font-semibold truncate">{r.carrier} · {r.service}</div>
                            {r.delivery_days && (
                              <div className="text-[10px] text-stone-500">{r.delivery_days} business day{r.delivery_days === 1 ? "" : "s"}</div>
                            )}
                          </div>
                          <div className="text-xs font-semibold">
                            {parseFloat(r.rate) === 0 ? <span className="text-[#3F5F3F] flex items-center gap-0.5"><Check className="w-3 h-3" strokeWidth={2.5} />FREE</span> : `$${parseFloat(r.rate).toFixed(2)}`}
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex justify-between font-sans text-sm mb-3">
                  <span className="text-stone-600">Shipping</span>
                  {selectedRate ? (
                    <span className="text-[#1A1C1A]">
                      {parseFloat(selectedRate.rate) === 0 ? "FREE" : `$${parseFloat(selectedRate.rate).toFixed(2)}`}
                    </span>
                  ) : (
                    <span className="text-stone-500 italic">Get rates above</span>
                  )}
                </div>
                <div className="flex justify-between font-sans text-sm mb-6">
                  <span className="text-stone-600">Tax</span>
                  <span className="text-stone-500 italic">Calculated at checkout</span>
                </div>
                <div className="pt-6 border-t border-stone-300/60 flex justify-between items-baseline">
                  <span className="font-serif text-xl">Total</span>
                  <span className="font-serif text-2xl">
                    ${(subtotal + (selectedRate ? parseFloat(selectedRate.rate) : 0)).toFixed(2)}
                  </span>
                </div>
                <button
                  data-testid={CART.checkoutButton}
                  onClick={checkout}
                  disabled={loading || !selectedRate}
                  className="mt-8 w-full bg-[#1A1C1A] text-[#F9F9F6] hover:bg-[#3C3E3C] px-8 py-4 rounded-full transition-all font-sans text-sm tracking-wider uppercase disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loading ? "Redirecting…" : "Secure Checkout"}
                </button>
                {!selectedRate && (
                  <p className="mt-3 text-xs text-[#8B6A1A] text-center font-medium">
                    Enter your ZIP above and pick a shipping option to continue.
                  </p>
                )}
                <p className="mt-4 text-xs text-stone-500 text-center font-light">
                  {freeShipApplied ? "You qualify for free shipping!" : "FREE shipping on orders $100+"}
                </p>
                <p className="mt-1 text-[10px] text-stone-400 text-center font-light">Powered by Stripe. Test card: 4242 4242 4242 4242</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
